import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WellnessProvider } from "./context/WellnessContext";
import { BluetoothProvider } from "./context/BluetoothContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import { BreakSuggestionModal, BreakModeOverlay } from "./components/WellnessComponents";
import DonationModal from "./components/DonationModal";
import InstallPWA from "./components/InstallPWA";
import { OnboardingTour } from "./components/OnboardingTour";
import { WhatsNewModal } from "./components/WhatsNewModal";

// Pages
const Landing = lazy(() => import("./pages/Landing"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ForgotPassword = lazy(() => import("./pages/PasswordReset").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/PasswordReset").then(m => ({ default: m.ResetPassword })));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Groups = lazy(() => import("./pages/Groups"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const Events = lazy(() => import("./pages/Events"));
const Messages = lazy(() => import("./pages/Messages"));
const Settings = lazy(() => import("./pages/Settings"));
const Wallet = lazy(() => import("./pages/Wallet"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const LiveStream = lazy(() => import("./pages/LiveStream"));
const Explore = lazy(() => import("./pages/Explore"));
const FriendRequests = lazy(() => import("./pages/FriendRequests"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AccountManagement = lazy(() => import("./pages/AccountManagement"));
const HelpGuide = lazy(() => import("./pages/HelpGuide"));

const PageLoader = () => (
  <div className="min-h-screen bg-[var(--app-bg)] flex items-center justify-center" data-testid="page-loader">
    <div className="animate-pulse">
      <div className="w-16 h-16 rounded-full gradient-warm"></div>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id check
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId?"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <ProtectedRoute>
            <Groups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <ProtectedRoute>
            <GroupDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Events />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:userId"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <SearchResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live"
        element={
          <ProtectedRoute>
            <LiveStream />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <FriendRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <ProtectedRoute>
            <Explore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live/:streamId"
        element={
          <ProtectedRoute>
            <LiveStream />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
          <WellnessProvider>
            <BluetoothProvider>
              <Suspense fallback={<PageLoader />}>
                <AppRouter />
              </Suspense>
              <BreakSuggestionModal />
              <BreakModeOverlay />
              <DonationModal />
              <OnboardingTour />
              <WhatsNewModal />
              <InstallPWA />
              <Toaster position="top-center" richColors />
            </BluetoothProvider>
          </WellnessProvider>
        </ThemeProvider>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
